import { isNavKeyImplied } from "@/components/settings/permissions/permissionModules";
import { fiscalDocumentsEnabledFromOrg } from "@/lib/fiscalDocumentsEnabled";
import { planAllowsPermission } from "@/lib/planCapabilities";
import type { Permission, User } from "@/types";

const FISCAL_PERMISSIONS = new Set<Permission>([
  "settings.fiscal",
  "finance.fiscal.view",
  "finance.fiscal.emit",
  "finance.fiscal.cancel",
]);

function userHasFullAccess(user: User): boolean {
  return user.isOwner || user.organization?.isOwner === true || user.isOrgAdmin;
}

export function can(user: User | null | undefined, permission: Permission): boolean {
  if (!user) return false;
  if (!planAllowsPermission(user, permission)) return false;
  if (FISCAL_PERMISSIONS.has(permission) && !fiscalDocumentsEnabledFromOrg(user.organization)) {
    return false;
  }
  if (userHasFullAccess(user)) return true;

  const permissions = user.permissions;
  if (permissions.includes(permission)) return true;
  return isNavKeyImplied(permission, new Set(user.permissions));
}

const DEFAULT_ROUTE_ORDER: Permission[] = [
  "nav.dashboard",
  "nav.projects",
  "project.clientBudgets.view",
  "nav.finance",
  "nav.settings",
];

/** True when the user can open at least one primary app area. */
export function hasNavigableAppAccess(user: User | null | undefined): boolean {
  if (!user) return false;
  if (userHasFullAccess(user)) return true;
  return DEFAULT_ROUTE_ORDER.some((key) => can(user, key));
}

export function resolveDefaultRoute(user: User | null | undefined): string {
  if (!user) return "/login";

  for (const key of DEFAULT_ROUTE_ORDER) {
    if (can(user, key)) {
      if (key === "nav.settings") return "/settings";
      if (key === "nav.dashboard") return "/dashboard";
      if (key === "nav.projects") return "/projects";
      if (key === "project.clientBudgets.view") return "/client-budgets";
      if (key === "nav.finance") return "/finance";
    }
  }

  return "/home";
}

/** Leave a secondary shell (account, settings) back to the user's primary app view. */
export function resolveAppExitRoute(user: User | null | undefined): string {
  if (!user) return "/login";
  if (can(user, "nav.dashboard")) return "/dashboard";
  if (can(user, "nav.projects")) return "/projects";
  if (can(user, "project.clientBudgets.view")) return "/client-budgets";
  if (can(user, "nav.finance")) return "/finance";
  if (can(user, "nav.settings")) return "/settings";
  return "/home";
}
